import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, PlayIcon, ClipboardDocumentListIcon, EnvelopeIcon, EyeIcon } from '@heroicons/react/24/outline';
import Button from '@/components/Button/Button';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { getPerformanceEvaluationCampaigns, PerformanceEvaluationCampaign, deletePerformanceEvaluationCampaign, updatePerformanceEvaluationStatus, resendCampaignEmails } from '../services/evaluationsService';
import { useToast } from '@/hooks/useToast';

export const EvaluationsListPage: React.FC = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState<PerformanceEvaluationCampaign[]>([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const data = await getPerformanceEvaluationCampaigns();
            setCampaigns(data);
        } catch (error) {
            console.error(error);
            toast.error('Erro', 'Não foi possível carregar as avaliações.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const handleDelete = async (id: string) => {
        if (!confirm('Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.')) return;

        try {
            await deletePerformanceEvaluationCampaign(id);
            toast.success('Sucesso', 'Campanha excluída com sucesso.');
            fetchCampaigns(); // Refresh
        } catch (error) {
            toast.error('Erro', 'Erro ao excluir campanha.');
        }
    };

    const handleActivate = async (id: string) => {
        if (!confirm('Deseja realmente ativar esta campanha? Ela ficará visível para os colaboradores.')) return;

        try {
            await updatePerformanceEvaluationStatus(id, 'active');
            toast.success('Sucesso', 'Campanha ativada com sucesso!');
            fetchCampaigns();
        } catch (error) {
            toast.error('Erro', 'Erro ao ativar campanha.');
        }
    };

    const handleResendEmails = async (id: string) => {
        if (!confirm('Deseja reenviar os e-mails de convite para todos os colaboradores desta campanha?')) return;

        try {
            await resendCampaignEmails(id);
            toast.success('Sucesso', 'E-mails de convite reenviados com sucesso!');
        } catch (error) {
            toast.error('Erro', 'Erro ao reenviar e-mails.');
        }
    };

    return (
        <div className="min-w-0 space-y-6">
            <SubPageHeader
                backTo="/people"
                icon={<ClipboardDocumentListIcon className="h-6 w-6" aria-hidden />}
                badge="Avaliações"
                title="Campanhas"
                description="Gerencie os ciclos e campanhas de avaliação de desempenho."
                actions={
                    <Button className="w-full justify-center sm:w-auto" onClick={() => navigate('new')}>
                        <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                        Nova Campanha
                    </Button>
                }
            />

            <div className="flex flex-col">
                <div className="-mx-4 px-4 pb-2 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-300 table-auto">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                            Ciclo / Campanha
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Questionário
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Período
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Status
                                        </th>
                                        <th scope="col" className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={5} className="text-center py-4 text-sm text-gray-500">
                                                Carregando...
                                            </td>
                                        </tr>
                                    ) : campaigns.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="text-center py-4 text-sm text-gray-500">
                                                Nenhuma avaliação encontrada.
                                            </td>
                                        </tr>
                                    ) : (
                                        campaigns.map((campaign) => {
                                            const displayStatus = campaign.status === 'active' ? 'Ativo'
                                                : campaign.status === 'draft' ? 'Rascunho'
                                                    : 'Encerrado';

                                            const statusColor = campaign.status === 'active' ? 'bg-green-100 text-green-800'
                                                : campaign.status === 'draft' ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-gray-100 text-gray-800';

                                            return (
                                                <tr key={campaign.id}>
                                                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`${campaign.id}`)}
                                                            className="block text-left hover:text-primary-700"
                                                        >
                                                            {campaign.questionnaireTitle || 'Avaliação Geral'}
                                                        </button>
                                                        <span className="text-xs text-gray-400 block">Criado em {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : '-'}</span>
                                                    </td>
                                                    <td className="px-3 py-4 text-sm text-gray-500">
                                                        {campaign.questionnaireTitle || '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {new Date(campaign.startDate).toLocaleDateString('pt-BR')} - {new Date(campaign.endDate).toLocaleDateString('pt-BR')}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${statusColor}`}>
                                                            {displayStatus}
                                                        </span>
                                                    </td>
                                                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-sm font-medium sm:pr-6">
                                                        <div className="ml-auto flex w-fit items-center justify-end gap-1">

                                                            {/* Activate Button (Draft Only) */}
                                                            {campaign.status === 'draft' && (
                                                                <button
                                                                    onClick={() => handleActivate(campaign.id!)}
                                                                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-green-700 hover:bg-green-50 hover:text-green-900"
                                                                    title="Ativar"
                                                                    aria-label={`Ativar campanha ${campaign.questionnaireTitle || 'Avaliação Geral'}`}
                                                                >
                                                                    <PlayIcon className="h-5 w-5" />
                                                                </button>
                                                            )}

                                                            {/* Resend Invites (Active Only) */}
                                                            {campaign.status === 'active' && (
                                                                <button
                                                                    onClick={() => handleResendEmails(campaign.id!)}
                                                                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-blue-700 hover:bg-blue-50 hover:text-blue-900"
                                                                    title="Reenviar Convites"
                                                                    aria-label={`Reenviar convites da campanha ${campaign.questionnaireTitle || 'Avaliação Geral'}`}
                                                                >
                                                                    <EnvelopeIcon className="h-5 w-5" />
                                                                </button>
                                                            )}

                                                            {/* Management Actions */}
                                                            <button
                                                                onClick={() => navigate(`${campaign.id}`)}
                                                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                                                                title="Acompanhar"
                                                                aria-label={`Acompanhar campanha ${campaign.questionnaireTitle || 'Avaliação Geral'}`}
                                                            >
                                                                <EyeIcon className="h-5 w-5" />
                                                            </button>

                                                            <button
                                                                onClick={() => navigate(`${campaign.id}/edit`)}
                                                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900"
                                                                title="Editar"
                                                                aria-label={`Editar campanha ${campaign.questionnaireTitle || 'Avaliação Geral'}`}
                                                            >
                                                                <PencilIcon className="h-5 w-5" />
                                                            </button>

                                                            <button
                                                                onClick={() => handleDelete(campaign.id!)}
                                                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-red-700 hover:bg-red-50 hover:text-red-900"
                                                                title="Excluir"
                                                                aria-label={`Excluir campanha ${campaign.questionnaireTitle || 'Avaliação Geral'}`}
                                                            >
                                                                <TrashIcon className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
